import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Facebook, CheckCircle2, XCircle, Loader2, RefreshCw, LogOut } from "lucide-react";
import { toast } from "sonner";

/**
 * Iter48 P0 — Facebook OAuth completion page.
 *
 * Flow:
 * 1. Read ?code + ?state (or ?error) from the URL that Meta redirected to.
 * 2. Validate state matches the one issued by /api/facebook/oauth/start
 *    (guards against CSRF). Reject on mismatch or error param.
 * 3. Exchange the code via /api/facebook/oauth/callback — backend does the
 *    secret exchange, we never see FACEBOOK_APP_SECRET.
 * 4. Show the list of pages the user CAN MANAGE (backend filters).
 * 5. Explicit page selection → POST /api/facebook/pages/select (encrypts
 *    the page access token server-side).
 * 6. Success view + Reconnect / Disconnect / Change page actions.
 *
 * Success/failure states, denied auth, and expired codes are all surfaced
 * with clear copy — never a bare error message.
 */
export default function FacebookOAuthCallback() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [phase, setPhase] = useState("exchanging");  // exchanging | selecting | success | error
  const [pages, setPages] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [selecting, setSelecting] = useState(null);
  const [connection, setConnection] = useState(null);

  useEffect(() => {
    (async () => {
      const err = params.get("error") || params.get("error_reason");
      const desc = params.get("error_description") || "";
      if (err) {
        setErrorMsg(`Facebook returned "${err}"${desc ? ` — ${desc}` : ""}. You can retry from Business Settings.`);
        setPhase("error");
        return;
      }
      const code = params.get("code");
      const state = params.get("state");
      const savedState = sessionStorage.getItem("fb_oauth_state");
      if (!code) {
        setErrorMsg("Missing OAuth code. Return to Business Settings and start again.");
        setPhase("error");
        return;
      }
      if (savedState && state && state !== savedState) {
        setErrorMsg("OAuth state mismatch — possible CSRF. Start again from Business Settings.");
        setPhase("error");
        return;
      }
      try {
        const { data } = await api.post("/facebook/oauth/callback", { code, state });
        const managed = (data.pages || []).filter((p) => p.access_token);
        if (managed.length === 0) {
          setErrorMsg("You don't manage any Facebook Pages with this account. Grant Page access on Facebook, then reconnect.");
          setPhase("error");
          return;
        }
        setPages(managed);
        setPhase("selecting");
      } catch (e) {
        const msg = e.response?.data?.detail || "Facebook token exchange failed. The code may have expired — retry from Business Settings.";
        setErrorMsg(msg);
        setPhase("error");
      } finally {
        sessionStorage.removeItem("fb_oauth_state");
      }
    })();
  }, [params]);

  const selectPage = async (p) => {
    setSelecting(p.id);
    try {
      const { data } = await api.post("/facebook/pages/select", {
        page_id: p.id, page_name: p.name, page_access_token: p.access_token,
      });
      setConnection(data);
      setPhase("success");
      toast.success(`Connected to Facebook Page: ${data.page_name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Page selection failed — try again");
    } finally { setSelecting(null); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect this Facebook Page? Existing published posts will remain on Facebook.")) return;
    try {
      await api.post("/facebook/disconnect");
      toast.success("Disconnected from Facebook");
      nav("/app/settings");
    } catch (e) { toast.error("Disconnect failed"); }
  };

  return (
    <div className="max-w-2xl mx-auto p-8" data-testid="facebook-oauth-callback-page">
      <div className="flex items-center gap-3 mb-6">
        <Facebook className="w-6 h-6 text-blue-600"/>
        <h1 className="font-heading text-2xl font-bold">Facebook connection</h1>
      </div>

      {phase === "exchanging" && (
        <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center card-crisp" data-testid="fb-oauth-exchanging">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF5A00] mx-auto mb-3"/>
          <div className="font-medium">Verifying with Facebook…</div>
          <div className="text-sm text-zinc-500 mt-1">Exchanging the authorization code for your Page list.</div>
        </div>
      )}

      {phase === "selecting" && (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp" data-testid="fb-oauth-page-picker">
          <div className="font-medium mb-1">Select the Facebook Page LeadForge should publish to</div>
          <div className="text-sm text-zinc-500 mb-4">Only Pages you can manage are shown. Access tokens are encrypted server-side.</div>
          <div className="space-y-2">
            {pages.map((p) => (
              <button
                key={p.id}
                onClick={() => selectPage(p)}
                disabled={!!selecting}
                className="w-full text-left border border-zinc-200 rounded-lg p-3 hover:border-blue-500 hover:bg-blue-50 transition-colors flex items-center justify-between"
                data-testid={`fb-page-option-${p.id}`}
              >
                <div>
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-zinc-500 font-mono-accent">{p.id}</div>
                </div>
                {selecting === p.id ? <Loader2 className="w-4 h-4 animate-spin"/> : <span className="text-blue-600 text-sm">Select →</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {phase === "success" && (
        <div className="bg-white border border-emerald-200 rounded-xl p-6 card-crisp" data-testid="fb-oauth-success">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600"/>
            <div className="font-medium">Connected!</div>
          </div>
          <div className="text-sm text-zinc-600 mb-4">
            LeadForge is now linked to <span className="font-medium">{connection?.page_name}</span>.
            Approved social posts can be published directly from the job page.
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => nav("/app/settings")} data-testid="fb-oauth-done">
              Back to Settings
            </Button>
            <Button variant="outline" onClick={() => nav(0)} data-testid="fb-oauth-change">
              <RefreshCw className="w-4 h-4 mr-1"/> Change Page
            </Button>
            <Button variant="outline" onClick={disconnect} data-testid="fb-oauth-disconnect">
              <LogOut className="w-4 h-4 mr-1"/> Disconnect
            </Button>
          </div>
        </div>
      )}

      {phase === "error" && (
        <div className="bg-white border border-red-200 rounded-xl p-6 card-crisp" data-testid="fb-oauth-error">
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="w-5 h-5 text-red-600"/>
            <div className="font-medium">Couldn't finish the connection</div>
          </div>
          <div className="text-sm text-zinc-600 mb-4">{errorMsg}</div>
          <Button onClick={() => nav("/app/settings")}>Back to Settings</Button>
        </div>
      )}
    </div>
  );
}
